// tabs/ThinkyBaaSIdentity.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faKey, faCheckCircle, faUserPlus, faTimesCircle } from "@fortawesome/free-solid-svg-icons";

// Dummy IAM data
const dummyUsers = [
  {
    id: 1,
    name: "Alice Kumar",
    role: "Admin",
    mfaEnabled: false,
    active: false,
    accessVerified: false,
    log: [],
    precondition: true, // can be used to simulate system readiness
  },
  {
    id: 2,
    name: "Bob Sharma",
    role: "Developer",
    mfaEnabled: false,
    active: false,
    accessVerified: false,
    log: [],
    precondition: true,
  },
  {
    id: 3,
    name: "Charlie Verma",
    role: "Viewer",
    mfaEnabled: false,
    active: false,
    accessVerified: false,
    log: [],
    precondition: false, // simulate incomplete onboarding
  },
];

const roles = ["Admin", "Developer", "Viewer"];

const ThinkyBaaSIdentity = () => {
  const [users, setUsers] = useState(dummyUsers.map(u => ({ ...u, log: u.log || [] })));

  // Activate user
  const activateUser = (id) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === id && !u.active
          ? {
              ...u,
              active: true,
              log: [...(u.log || []), { event: "User Activated", time: new Date().toLocaleTimeString() }],
            }
          : u
      )
    );
  };

  // Enable MFA
  const enableMFA = (id) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === id && u.active && !u.mfaEnabled
          ? {
              ...u,
              mfaEnabled: true,
              log: [...(u.log || []), { event: "MFA Enabled", time: new Date().toLocaleTimeString() }],
            }
          : u
      )
    );
  };

  // Verify Access
  const verifyAccess = (id) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === id && u.active && u.mfaEnabled && !u.accessVerified
          ? {
              ...u,
              accessVerified: true,
              log: [...(u.log || []), { event: "Access Verified", time: new Date().toLocaleTimeString() }],
            }
          : u
      )
    );
  };

  // Revoke Access
  const revokeAccess = (id) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === id
          ? {
              ...u,
              active: false,
              mfaEnabled: false,
              accessVerified: false,
              log: [...(u.log || []), { event: "Access Revoked", time: new Date().toLocaleTimeString() }],
            }
          : u
      )
    );
  };

  // Change role
  const changeRole = (id, newRole) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === id
          ? {
              ...u,
              role: newRole,
              log: [...(u.log || []), { event: `Role changed to ${newRole}`, time: new Date().toLocaleTimeString() }],
            }
          : u
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faUserShield} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Identity & Access Management (IAM)</h2>
      </div>

      <p className="text-gray-300 text-sm">
        This page simulates a blockchain <span className="font-semibold">Identity & Access Management workflow</span>:
        Activate → Enable MFA → Verify Access → Role Assignment → Revoke Access. Event logs show all actions.
      </p>

      <div className="space-y-4">
        {users.map(u => (
          <div key={u.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <div className="flex justify-between items-start sm:items-center flex-wrap gap-2">
              <div>
                <h3 className="text-xl font-semibold mb-1">{u.name}</h3>
                <p className="text-gray-400 mb-1">Role: {u.role}</p>
                <p className="text-gray-400 mb-1">
                  Status:{" "}
                  <span className={`${u.active ? "text-green-400" : "text-yellow-400"}`}>
                    {u.active ? "Active ✅" : "Inactive ⏳"}
                  </span>
                </p>
                <p className="text-gray-400 mb-1">
                  MFA:{" "}
                  <span className={`${u.mfaEnabled ? "text-green-400" : "text-yellow-400"}`}>
                    {u.mfaEnabled ? "Enabled ✅" : "Disabled ⏳"}
                  </span>
                </p>
                <p className="text-gray-400 mb-1">
                  Access Verified:{" "}
                  <span className={`${u.accessVerified ? "text-green-400" : "text-yellow-400"}`}>
                    {u.accessVerified ? "✅" : "⏳"}
                  </span>
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  disabled={u.active}
                  onClick={() => activateUser(u.id)}
                  className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                    u.active ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="mr-1" /> Activate
                </button>

                <button
                  disabled={!u.active || u.mfaEnabled}
                  onClick={() => enableMFA(u.id)}
                  className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                    !u.active || u.mfaEnabled ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  <FontAwesomeIcon icon={faKey} className="mr-1" /> Enable MFA
                </button>

                <button
                  disabled={!u.active || !u.mfaEnabled || u.accessVerified}
                  onClick={() => verifyAccess(u.id)}
                  className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                    !u.active || !u.mfaEnabled || u.accessVerified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
                </button>

                <select
                  disabled={!u.active}
                  value={u.role}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className="w-32 text-sm px-2 py-1 rounded-lg bg-gray-700 text-white border border-gray-600"
                >
                  {roles.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>

                <button
                  onClick={() => revokeAccess(u.id)}
                  className="w-32 text-sm px-3 py-1 rounded-lg font-semibold bg-red-600 hover:bg-red-700 transition"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Revoke
                </button>
              </div>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!u.log || u.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {u.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSIdentity;
